import Styles from './styles.module.css';
import React, { useEffect, useState } from 'react';
import { IoBookmark, IoBookmarkOutline, IoCheckmark, IoClose } from 'react-icons/io5';
import { doc, getDoc, getFirestore, setDoc, updateDoc } from 'firebase/firestore';
import {
  ApplicantRow, EVENT_KIND_LABELS, EVENT_STATUS_STEPS, EntryRow, EventEntry, StatusMessageKey, StatusTone,
  applicationMessageKey, eventMessageKey, eventShortLabel, eventStatusLabel, eventStatusTone, eventTargetLabel,
  examinationLabel, examinationTone, resolveStatusMessages, studentAvatarUrl, visitMessageKey, visitStatusTone,
} from '../types';
import { useAppSelector } from 'src/salon/app/hook';
import MessageTab, { MessageDraft } from './MessageTab';
import MyCardTab from './MyCardTab';
import ResumeTab from './ResumeTab';
import EvaluationTab from './EvaluationTab';
import DefaultImg from '../../../../image/default/account.png';

export type TabKey = 'message' | 'mycard' | 'resume' | 'evaluation';

type Props = {
  entry: EntryRow;
  initialTab: TabKey;
  onClose: () => void;
  onApplicantChanged: (patch: Partial<ApplicantRow>) => void;
  onVisitStatusChanged: (status: string) => void;
  // 応募が締めた募集回に入っていれば、その回の名前。締めた回では選考段階を動かせず、
  // 結論なしの学生だけ採用・不採用・辞退を選べる。結論が出ている学生は何も変えられない。
  closedPeriodName?: string;
  // この学生(チャット)の会社説明会・クーポンの申込(詳細の上部に印を出す)
  chatEvents?: EventEntry[];
};

// 表示順はこの並びのまま、種別に応じて不要なタブだけ除外する
// (見学: メッセージ→My Card→管理・集計、応募: メッセージ→My Card→履歴書→管理・集計)。
// 見学の申込内容(希望店舗・希望日時・気になっていること)は以前「希望内容」タブで見ていたが、
// 日程調整の後に見返すことはほぼ無いため、メッセージ内の申込カード(VisitRequestCard)で見る形にした。
const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: 'message', label: 'メッセージ' },
  { key: 'mycard', label: 'My Card' },
  { key: 'resume', label: '履歴書' },
  { key: 'evaluation', label: '管理・集計' },
];

// 学生詳細パネル(右サイドドロワー)。一覧の行クリック/アイコンクリックで開き、
// 一覧は左側に残したまま同時に閲覧できる(モーダル・ポップアップにはしない)。
// タブ切り替えのみで完結させ、別ページ遷移は発生させない。
//
// 種別・学校名・進捗(ステップ表示)は、どのタブを開いていても常に見えるようヘッダー側にまとめ、
// タブ本体はメッセージ/My Card/履歴書/評価の4つに絞っている。
// メモは一覧の各行のメモアイコンから開く(メッセージを書きながらメモを見ることは少ないため、
// チャットの上にはメモ・日程を出さず、メッセージの表示領域を広く取る)。
type Step = { value: string; label: string };

// 進捗ステップ。終わった段階はチェック、今の段階はステータスの色で強調、これからの段階はグレー。
// 各段階を押すとそのステータスに変わる。キャンセル/不採用/辞退は流れから外れる終了状態なので、
// 右端の小さなボタンにして、押したときに確認を出す。
const StatusStepper: React.FC<{
  steps: Step[];
  terminals: Step[];
  current: string;
  tone: StatusTone;
  disabled: boolean;
  disabledText: string;
  onSelect: (value: string) => void;
}> = ({ steps, terminals, current, tone, disabled, disabledText, onSelect }) => {
  const isClosed = terminals.some((t) => t.value === current);
  const currentIndex = steps.findIndex((s) => s.value === current);

  if (disabled) {
    return <p className={Styles.stepperEmpty}>{disabledText}</p>;
  }

  return (
    <div className={Styles.stepper}>
      <ol className={isClosed ? `${Styles.steps} ${Styles.stepsClosed}` : Styles.steps}>
        {steps.map((step, i) => {
          const state = isClosed ? 'upcoming' : i < currentIndex ? 'done' : i === currentIndex ? 'current' : 'upcoming';
          return (
            <li
              key={step.value}
              className={[Styles.step, Styles[`step_${state}`], state === 'current' ? Styles[`stepTone_${tone}`] : '', i <= currentIndex && !isClosed ? Styles.stepReached : ''].filter(Boolean).join(' ')}
            >
              <button
                type="button"
                className={Styles.stepButton}
                onClick={() => step.value !== current && onSelect(step.value)}
                aria-current={state === 'current' ? 'step' : undefined}
              >
                <span className={Styles.stepDot}>{state === 'done' ? <IoCheckmark size={12} /> : i + 1}</span>
                <span className={Styles.stepLabel}>{step.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
      <div className={Styles.stepTerminals}>
        {terminals.map((terminal) => {
          const active = current === terminal.value;
          return (
            <button
              key={terminal.value}
              type="button"
              className={active ? `${Styles.stepTerminal} ${Styles.stepTerminalActive}` : Styles.stepTerminal}
              onClick={() => {
                if (active) return;
                if (window.confirm(`「${terminal.label}」にしますか？`)) onSelect(terminal.value);
              }}
            >
              {active ? `${terminal.label}済み` : terminal.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const VISIT_STEPS: Step[] = [
  { value: 'requested', label: '日程調整中' },
  { value: 'confirmed', label: '日程確定' },
  { value: 'completed', label: '見学済み' },
];
const VISIT_TERMINALS: Step[] = [{ value: 'cancelled', label: 'キャンセル' }];
const APPLICATION_TERMINALS: Step[] = [{ value: '7', label: '不採用' }, { value: '8', label: '辞退' }];
// 締めた募集回で結論なしの学生が選べる結論
const CLOSED_CONCLUSIONS = [{ value: 6, label: '採用' }, { value: 7, label: '不採用' }, { value: 8, label: '辞退' }];

// 応募のステップ。通常は 1次→2次→3次→採用。今が4次・5次のときだけその段階も出す。
const applicationSteps = (current: number): Step[] => {
  const stages = [1, 2, 3];
  if (current >= 4 && current <= 5) for (let s = 4; s <= current; s++) stages.push(s);
  return [...stages.map((s) => ({ value: String(s), label: `${s}次選考` })), { value: '6', label: '採用' }];
};

const StudentDetailPanel: React.FC<Props> = ({ entry, initialTab, onClose, onApplicantChanged, onVisitStatusChanged, closedPeriodName = '', chatEvents = [] }) => {

  const company = useAppSelector((state) => state.company.company);
  const [ tab, setTab ] = useState<TabKey>(initialTab);
  // ステータス変更時にメッセージの入力欄へ入れるテンプレート
  const [ draft, setDraft ] = useState<MessageDraft | null>(null);

  // 一覧で別の学生をクリックした場合、開いた瞬間のタブ(通常はメッセージ)に戻す
  useEffect(() => {
    setTab(initialTab);
    setDraft(null);
  }, [entry.id, initialTab]);

  // 学校名の後ろに出す学科・学年(users/{uid}.education)
  const [ education, setEducation ] = useState<{ department: string; grade: string }>({ department: '', grade: '' });
  useEffect(() => {
    let cancelled = false;
    setEducation({ department: '', grade: '' });
    if (!entry.chatRoom.user.uid) return;
    getDoc(doc(getFirestore(), 'users', entry.chatRoom.user.uid))
      .then((snap) => {
        if (cancelled) return;
        const e = snap.data()?.education ?? {};
        setEducation({ department: e.department ?? '', grade: e.grade ?? '' });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [entry.chatRoom.user.uid]);

  // 申込日は応募・見学を申し込んだ日(見学から応募した学生・再応募した学生は応募した日)
  const appliedAt = entry.chatRoom.meta.createdAt;

  const avatarUrl = studentAvatarUrl(entry.chatRoom);

  const isVisit = entry.kind === 'visit';
  // 会社説明会・クーポンの申込の行(チャットは entry.chatRoom、申込は entry.event)
  const event = entry.event;
  const examination = entry.applicant?.examination ?? 1;
  const isConcluded = examination === 6 || examination === 7 || examination === 8;

  // 見学は applicants ドキュメントを持たない(entry.applicant は常にnull)ため、
  // 「管理・集計」タブ(EvaluationTab/EvaluationSummary)に渡す最低限のApplicantRow相当を
  // その場で組み立てる。examination=0は16.judging側の見学専用の固定値と揃えている。
  const evaluationApplicant: ApplicantRow | null = entry.applicant ?? (isVisit ? {
    id: entry.id,
    recruitId: entry.chatRoom.company.recruitId,
    name: entry.name,
    time: entry.chatRoom.meta.createdAt,
    examination: 0,
    memo: entry.chatRoom.meta.memo,
    examList1: [],
    examList2: [],
    examList3: [],
    examList4: [],
    examList5: [],
  } : null);

  // 応募設定の「ステータス変更時のメッセージ」に本文があれば、メッセージタブを開いて入力欄に入れる。
  // 送信は人が確認してから行う。テンプレートが読めなくてもステータスの変更自体は終わっているので、何もしない。
  async function openStatusMessage(key: StatusMessageKey | null) {
    if (!key) return;
    try {
      const recruitId = entry.chatRoom.company.recruitId || `${company.id}_2027`;
      const snap = await getDoc(doc(getFirestore(), 'recruits', recruitId));
      const template = resolveStatusMessages(snap.data()?.statusMessages)[key];
      if (!template.trim()) return;
      setDraft({ text: template, id: Date.now() });
      setTab('message');
    } catch (e) {
      console.error('ステータス変更時のメッセージを読み込めませんでした', e);
    }
  }

  async function changeExamination(value: number) {
    if (!entry.applicant) return;
    const from = entry.applicant.examination ?? 1;
    const db = getFirestore();
    await setDoc(doc(db, 'applicants', entry.applicant.id), { examination: value }, { merge: true });
    onApplicantChanged({ examination: value });
    await openStatusMessage(applicationMessageKey(from, value));
  }

  async function changeVisitStatus(value: string) {
    if (!entry.visit) return;
    const db = getFirestore();
    await setDoc(doc(db, 'chats', entry.id, 'visits', entry.visit.id), { status: value }, { merge: true });
    onVisitStatusChanged(value);
    await openStatusMessage(visitMessageKey(value));
  }

  // 応募・選考(6.manage)にもある「保存」(ブックマーク)。以前は行アイコンだったが、
  // ここではタブ列の末尾に置く。一覧はchatsのonSnapshotを購読しているため、
  // 書き込み後は自動的にentry.chatRoom.meta.saveへ反映される。
  // 会社説明会・クーポンの申込の状態を変える(eventEntries。一覧は購読しているので自動で反映される)
  async function changeEventStatus(value: string) {
    if (!event) return;
    await updateDoc(doc(getFirestore(), 'eventEntries', event.id), { status: value, updatedAt: new Date() });
    await openStatusMessage(eventMessageKey(event.kind, value));
  }

  async function toggleSave(next: boolean) {
    const db = getFirestore();
    await updateDoc(doc(db, 'chats', entry.chatRoom.id), { 'meta.save': next });
  }

  // 履歴書は応募のみ。会社説明会・クーポンはメッセージと My Card だけ(審査・評価はしない)。
  const visibleTabs = ALL_TABS.filter((t) => {
    if (event) return t.key === 'message' || t.key === 'mycard';
    if (t.key === 'resume') return !isVisit;
    return true;
  });
  // この学生のほかの説明会・クーポンの申込(今開いている申込は除く)
  const otherEvents = chatEvents.filter((ev) => ev.id !== event?.id);

  return (
    <div className={Styles.drawer}>
      <div className={Styles.header}>
        <div className={Styles.headerTop}>
          <div className={Styles.headerProfile}>
            <img
              className={Styles.headerAvatar}
              src={avatarUrl}
              alt=""
              onError={(e) => {
                // 写真が無い(読み込めない)ときは「画像なし」の画像を出す(空白だと何も分からないため)
                e.currentTarget.onerror = null;
                e.currentTarget.src = DefaultImg;
              }}
            />
            <div className={Styles.headerText}>
              <h3 className={Styles.headerName}>{entry.name || '名前未設定'}</h3>
              <p className={Styles.headerSchool}>
                {[entry.schoolName || '学校名未設定', [education.department, education.grade].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
              </p>
              <div className={Styles.headerSubRow}>
                <span className={event ? Styles.kindBadgeEvent : isVisit ? Styles.kindBadgeVisit : Styles.kindBadgeApplication}>
                  {event ? EVENT_KIND_LABELS[event.kind] : isVisit ? 'サロン見学' : '応募'}
                </span>
                <span className={Styles.headerDate}>申込 {appliedAt.getMonth() + 1}/{appliedAt.getDate()}</span>
              </div>
            </div>
          </div>
          <div className={Styles.headerActions}>
            {entry.chatRoom.meta.save ? (
              <IoBookmark className={Styles.headerSaveIconActive} title="保存を外す" onClick={() => toggleSave(false)} />
            ) : (
              <IoBookmarkOutline className={Styles.headerSaveIcon} title="保存する" onClick={() => toggleSave(true)} />
            )}
            <button type="button" className={Styles.closeBtn} onClick={onClose}>
              <IoClose size={22} />
            </button>
          </div>
        </div>

        {/* 会社説明会・クーポン: どれに申し込んだか */}
        {event && (
          <div className={Styles.eventTarget}>
            <span className={Styles.eventTargetLabel}>{EVENT_KIND_LABELS[event.kind]}</span>
            <span className={Styles.eventTargetName}>{eventTargetLabel(event)}</span>
            {event.kind === 'infoSession' && event.target.place && <span className={Styles.eventTargetSub}>場所：{event.target.place}</span>}
            {event.kind === 'coupon' && !!event.target.priceBefore && (
              <span className={Styles.eventTargetSub}>通常 ¥{event.target.priceBefore.toLocaleString('ja-JP')}</span>
            )}
          </div>
        )}
        {/* この学生のほかの説明会・クーポンの申込 */}
        {otherEvents.length > 0 && (
          <div className={Styles.otherEvents}>
            {otherEvents.map((ev) => (
              <span key={ev.id} className={Styles.otherEvent}>
                {eventShortLabel(ev)}・{eventStatusLabel(ev.kind, ev.status)}
              </span>
            ))}
          </div>
        )}

        {event ? (
          <StatusStepper
            steps={EVENT_STATUS_STEPS[event.kind].map((st) => ({ value: st.value, label: st.label }))}
            terminals={[{ value: 'cancelled', label: 'キャンセル' }]}
            current={event.status}
            tone={eventStatusTone(event.status)}
            disabled={false}
            disabledText=""
            onSelect={(v) => changeEventStatus(v)}
          />
        ) : isVisit ? (
          <StatusStepper
            steps={VISIT_STEPS}
            terminals={VISIT_TERMINALS}
            current={entry.visit?.status ?? ''}
            tone={visitStatusTone(entry.visit?.status)}
            disabled={!entry.visit}
            disabledText="見学の申し込み情報がないため、ステータスを変更できません。"
            onSelect={(v) => changeVisitStatus(v)}
          />
        ) : closedPeriodName && entry.applicant ? (
          isConcluded ? (
            <p className={Styles.stepperEmpty}>
              {closedPeriodName}は締め済みのため、ステータスは変更できません（結果：{examinationLabel(examination)}）。
            </p>
          ) : (
            // 締めた回で結論が出ていない学生。選考段階は動かせず、結論だけ選べる。
            <div className={Styles.closedConclusion}>
              <p>{closedPeriodName}は締め済みです。この学生は結論が出ていないため、結論だけ選べます（選考段階は変更できません）。</p>
              <div className={Styles.closedConclusionButtons}>
                {CLOSED_CONCLUSIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={Styles.stepTerminal}
                    onClick={() => {
                      if (window.confirm(`「${c.label}」にしますか？結論を出すと、この学生のステータスは以後変更できません。`)) changeExamination(c.value);
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )
        ) : (
          <StatusStepper
            steps={applicationSteps(examination)}
            terminals={APPLICATION_TERMINALS}
            current={String(examination)}
            tone={examinationTone(examination)}
            disabled={!entry.applicant}
            disabledText="応募の情報を読み込めないため、ステータスを変更できません。"
            onSelect={(v) => changeExamination(Number(v))}
          />
        )}

        <div className={Styles.tabs}>
          {visibleTabs.map((t) => (
            <span
              key={t.key}
              className={tab === t.key ? `${Styles.tab} ${Styles.tabActive}` : Styles.tab}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </span>
          ))}
        </div>
      </div>

      <div className={Styles.body}>
        {tab === 'message' && (
          <MessageTab chatRoom={entry.chatRoom} draft={draft} onDraftUsed={() => setDraft(null)} />
        )}

        {tab === 'mycard' && (
          <MyCardTab uid={entry.chatRoom.user.uid} />
        )}

        {tab === 'resume' && (
          <ResumeTab applicationId={entry.id} />
        )}

        {tab === 'evaluation' && (
          evaluationApplicant
            ? <EvaluationTab applicant={evaluationApplicant} onChanged={onApplicantChanged} isVisit={isVisit} />
            : <p className={Styles.empty}>評価情報を取得できませんでした。</p>
        )}
      </div>

    </div>
  );
};

export default StudentDetailPanel;
